import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/utilities/cn'

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl border border-transparent text-sm font-medium tracking-tight transition-[color,background,box-shadow,border] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-60 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0 aria-invalid:border-destructive aria-invalid:ring-destructive/30",
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow-[0_2px_10px_rgba(31,43,46,0.12)] hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground shadow-[0_2px_10px_rgba(159,41,41,0.25)] hover:bg-destructive/90',
        outline:
          'border-border bg-transparent text-foreground hover:border-foreground/50 hover:bg-card/60',
        secondary:
          'border-secondary/20 bg-secondary/10 text-secondary hover:border-secondary/40 hover:bg-secondary/15',
        ghost: 'border-transparent bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground',
        link: 'border-transparent bg-transparent text-primary underline-offset-4 hover:underline',
        nav: 'border-transparent bg-transparent px-0 py-0 text-sm font-medium text-muted-foreground hover:text-foreground [&.active]:text-foreground',
      },
      size: {
        clear: '',
        default: 'h-10 px-4 has-[>svg]:px-3',
        sm: 'h-9 gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-11 px-6 has-[>svg]:px-4',
        icon: 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }

function Button({ className, variant, size, asChild = false, ...props }: ButtonProps) {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
